import {
  formatDateISO,
  getDateUtil,
  parseDateISO,
} from '@zorro/shared/formatters';
import {
  IDateRangeInputProps,
  IMonthPickerInputProps,
  MonthPickerInput,
} from '@zorro/web/design-system';
import { useMonolithQuery } from '@zorro/web/ui-utils';
import { ForwardedRef, forwardRef } from 'react';
import { ControllerRenderProps, Path } from 'react-hook-form';

import { useAnalytics } from '../Analytics';

const dateUtil = getDateUtil();

type CoveragePeriodFieldValues = {
  effectiveDates: [Date | null, Date | null];
};
type Props<T extends CoveragePeriodFieldValues, U extends Path<T>> = Omit<
  ControllerRenderProps<T, U>,
  'ref' | 'preset'
> &
  Omit<
    IDateRangeInputProps &
      IMonthPickerInputProps<'range'> & {
        employeeId: string;
        onboardingPeriodId: string;
      },
    'type' | 'preset'
  >;

export const CoveragePeriodInput = forwardRef(
  <T extends CoveragePeriodFieldValues, U extends Path<T>>(
    { employeeId, onboardingPeriodId, __onPresetSelect, ...field }: Props<T, U>,
    ref: ForwardedRef<HTMLButtonElement>
  ) => {
    const { sendEvent } = useAnalytics();
    const { data: employee } = useMonolithQuery({
      method: 'employeesControllerFindOne',
      params: [employeeId],
    });
    const { data: onboardingPeriod } = useMonolithQuery({
      method: 'onboardingPeriodsControllerFindOne',
      params: [employeeId, onboardingPeriodId],
    });

    if (!employee || !onboardingPeriod) return null;

    const [enrollmentEffectiveFrom, enrollmentEffectiveUntil] = [
      parseDateISO(onboardingPeriod.enrollmentEffectiveFrom),
      parseDateISO(onboardingPeriod.enrollmentEffectiveUntil),
    ];

    const minDate = employee.eligibleFrom
      ? dateUtil.max(
          enrollmentEffectiveFrom,
          parseDateISO(employee.eligibleFrom)
        )
      : enrollmentEffectiveFrom;

    const maxDate = employee.eligibleUntil
      ? dateUtil.min(
          enrollmentEffectiveUntil,
          parseDateISO(employee.eligibleUntil)
        )
      : enrollmentEffectiveUntil;

    return (
      <MonthPickerInput
        {...field}
        ref={ref}
        placeholder="Select coverage period dates"
        minDate={minDate.toDate()}
        maxDate={maxDate.toDate()}
        type="range"
        size="md"
        allowSingleDateInRange
        onChange={async (value) => {
          field.onChange(value);
          if (Array.isArray(value) && value[0] && value[1]) {
            await sendEvent('coverage_period_selected', {
              onboarding_period_id: onboardingPeriodId,
              from_date_range: formatDateISO(value[0]),
              to_date_range: formatDateISO(value[1]),
            });
          }
        }}
      />
    );
  }
);

CoveragePeriodInput.displayName = 'CoveragePeriodInput';
